import {
  Spinner,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { routes } from "@libs/routing/routes.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { PageLink } from "@ui-components/PageLink/PageLink.tsx";
import { When } from "@ui-components/withPerm.tsx";

import physitrackLogo from "./assets/physitrack_logo.svg";
import xeroLogoBlue from "./assets/xeroLogoBlue.svg";
import { IntegrationScreenContext } from "./context/IntegrationScreenContext.ts";
import { IntegrationScreenHelper } from "./context/IntegrationScreenHelper.ts";

const IntegrationsScreen: React.FC = () => {
  const root = useStores();
  const { routing } = useStores();
  const { practice, core } = useStores();

  const fetch = () =>
    Promise.all([
      practice.getOrgUnit(core.location?.id),
      root.customIntegrationXero.getXeroTenantInfo()
    ]);

  return (
    <DataFetcher fetch={fetch} fallback={<Spinner />}>
      {([orgUnit, xeroTenant]) => {
        const physitrackEnabled = !!orgUnit?.orgUnitEnabledPhysitrack?.enabled;
        const xeroEnabled = !!orgUnit?.orgUnitEnabledXero?.enabled;
        const xeroAuthorised = !!xeroTenant.tenants.length;

        return (
          <IntegrationScreenContext.Provider
            value={new IntegrationScreenHelper(root)}
          >
            <Stack tokens={{ childrenGap: 24 }}>
              <PageLink
                getImage={physitrackLogo}
                onClick={() =>
                  routing.push(routes.settings.integrations.physitrack.path({}))
                }
                headingText=""
                children={
                  <Stack horizontal horizontalAlign="end">
                    {physitrackEnabled && (
                      <TextBadge
                        styles={{
                          root: {
                            marginRight: 8
                          }
                        }}
                        badgeSize={TextBadgeSize.small}
                        badgeColor={TextBadgeColor.green}
                      >
                        Enabled
                      </TextBadge>
                    )}
                  </Stack>
                }
              />
              <When permission={[Permission.ManageIntegrationsAllowed]}>
                <PageLink
                  getImage={xeroLogoBlue}
                  onClick={() =>
                    routing.push(routes.settings.integrations.xero.path({}))
                  }
                  headingText=""
                  children={
                    <Stack horizontal horizontalAlign="end">
                      {xeroEnabled && (
                        <TextBadge
                          styles={{
                            root: { marginRight: 8 }
                          }}
                          badgeSize={TextBadgeSize.small}
                          badgeColor={
                            xeroAuthorised
                              ? TextBadgeColor.green
                              : TextBadgeColor.yellow
                          }
                        >
                          {xeroAuthorised
                            ? "Enabled"
                            : "Configuration incomplete"}
                        </TextBadge>
                      )}
                    </Stack>
                  }
                />
              </When>
            </Stack>
          </IntegrationScreenContext.Provider>
        );
      }}
    </DataFetcher>
  );
};

// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default IntegrationsScreen;
